import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SCHEDULE_PATH } from "@/constants/links";

const baseNavLinks = [
  { label: "Home", hash: "", sectionId: "hero" },
  { label: "Services", hash: "#services", sectionId: "services" },
  { label: "Team", hash: "#team", sectionId: "team" },
  { label: "Insights", hash: "#insights", path: "/insights", sectionId: "insights" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const hasMounted = useRef(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isInsights = location.pathname === "/insights" || location.pathname.startsWith("/insights/");

  // Scroll progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const navLinks = baseNavLinks.map((link) => ({
    label: link.label,
    href: link.path ? link.path : (isHome ? (link.hash || "#") : `/${link.hash}`),
    sectionId: link.sectionId,
    path: link.path,
  }));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // IntersectionObserver for active section detection on homepage
  useEffect(() => {
    if (!isHome) return;

    const sectionIds = ["hero", "services", "team", "insights"];
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [isHome]);

  // Set active section based on route when not on homepage
  useEffect(() => {
    if (isInsights) {
      setActiveSection("insights");
    } else if (!isHome) {
      setActiveSection("");
    }
  }, [isHome, isInsights]);

  const isLinkActive = useCallback((link: { sectionId: string; path?: string }) => {
    if (link.path && isInsights) return true;
    if (isHome && link.sectionId === activeSection) return true;
    return false;
  }, [isHome, isInsights, activeSection]);

  return (
    <>
      {/* Scroll progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] bg-primary z-[60] origin-left"
        style={{ scaleX }}
      />

      <motion.nav
        initial={hasMounted.current ? false : { y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`fixed top-[3px] left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background/90 backdrop-blur-lg shadow-card border-b border-border" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between py-4 px-6">
          <a href={isHome ? "#" : "/"} className="flex items-center gap-2">
            <img src="/images/tn-logo.png" alt="TechNeed logo" className="h-10 w-auto" width={40} height={40} />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="relative text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                {link.label}
                {isLinkActive(link) && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            ))}
            <Button size="sm" className="rounded-full px-6" asChild>
              <Link to={SCHEDULE_PATH}>Let's Talk</Link>
            </Button>
          </div>

          <button
            className="md:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-background border-b border-border"
            >
              <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className={`text-sm font-medium transition-colors ${
                      isLinkActive(link) ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
                <Button size="sm" className="rounded-full w-fit px-6" asChild>
                  <Link to={SCHEDULE_PATH} onClick={() => setMobileOpen(false)}>Let's Talk</Link>
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
